import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminSearchTable = ({ columns, data, rowsPerPage = 10 }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const navigate = useNavigate();
    const [psm, setPsm] = useState(null);
    const [loadingSettings, setLoadingSettings] = useState(true);

    //ENV Variables
    const apiURL = import.meta.env.VITE_API;

    const totalEntries = data.length;
    const totalPages = Math.ceil(totalEntries / rowsPerPage);

    useEffect(() => {
        const fetchPortalSettings = async () => {
            try {
                const res = await fetch(`${apiURL}/shared/getPortalSettings`, {
                    method: 'GET',
                    credentials: 'include',
                });

                if (!res.ok) throw new Error('Failed to fetch portal settings');

                const data = await res.json();
                
                setPsm(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingSettings(false);
            }
        };
        fetchPortalSettings();
    }, []);


    const sortedData = useMemo(() => {
        if (!sortConfig.key) return [...data];

        const col = columns.find(c => c.accessor === sortConfig.key);
        const sortType = col?.sortType;      // 'date' | 'number' | 'currency' | 'string'
        const sortable = col?.sortable !== false; // default true

        if (!sortable) return [...data];

        const sorted = [...data].sort((a, b) => {
            let aVal = a[sortConfig.key] ?? '';
            let bVal = b[sortConfig.key] ?? '';

            const toNumber = (val) => {
                if (val == null) return null;
                if (typeof val === 'number') return val;

                let str = String(val).trim();

                // Detect accounting-style negatives: ($12.34) or (12.34)
                let isParenNegative = false;
                const parenMatch = /^\((.*)\)$/.exec(str);
                if (parenMatch) {
                    isParenNegative = true;
                    str = parenMatch[1]; // inner content without parentheses
                }

                // Strip currency symbols, commas, spaces, etc.
                const cleaned = str.replace(/[^0-9.-]+/g, '');

                const num = parseFloat(cleaned);
                if (Number.isNaN(num)) return null;

                return isParenNegative ? -num : num;
            };

            // helper: date parsing
            const toDate = (val) => {
                if (!val) return new Date(0);
                const d = new Date(val);
                return Number.isNaN(d.getTime()) ? new Date(0) : d;
            };

            // 1) Explicit sortType overrides
            if (sortType === 'date') {
                const aDate = toDate(aVal);
                const bDate = toDate(bVal);
                return aDate - bDate;
            }

            if (sortType === 'currency' || sortType === 'number') {
                const numA = toNumber(aVal);
                const numB = toNumber(bVal);

                if (numA !== null && numB !== null) {
                    if (numA < numB) return -1;
                    if (numA > numB) return 1;
                    return 0;
                }
                // if one/both fail numeric, fall through to string
            }

            if (sortType === 'string') {
                return aVal.toString().localeCompare(
                    bVal.toString(),
                    undefined,
                    { numeric: true }
                );
            }

            // 2) Fallback behavior (keeps your old heuristic)
            const keyLower = sortConfig.key.toLowerCase();
            if (keyLower.includes('date')) {
                const aDate = toDate(aVal);
                const bDate = toDate(bVal);
                return aDate - bDate;
            }

            // try numeric first
            const numA = toNumber(aVal);
            const numB = toNumber(bVal);
            if (numA !== null && numB !== null) {
                if (numA < numB) return -1;
                if (numA > numB) return 1;
                return 0;
            }

            // otherwise string compare
            return aVal.toString().localeCompare(
                bVal.toString(),
                undefined,
                { numeric: true }
            );
        });

        return sortConfig.direction === 'asc' ? sorted : sorted.reverse();
    }, [data, sortConfig, columns]);

    const paginatedData = sortedData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const handleSort = (key) => {
        const col = columns.find(c => c.accessor === key);
        if (col?.sortable === false) return;

        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [data]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    const from = (currentPage - 1) * rowsPerPage + 1;
    const to = Math.min(currentPage * rowsPerPage, totalEntries);

    if (loadingSettings || !psm) return null;

    return (
        <div className="paginated-table-container">
            <table className="paginated-table">
                <thead>
                    <tr>
                        {columns.map(col => (
                            <th
                                key={col.accessor}
                                onClick={() => handleSort(col.accessor)}
                                style={{
                                    cursor: col?.sortable === false ? 'default' : 'pointer'
                                }}
                            >
                                {col.label}
                                {col?.sortable === false ? '' : getSortIndicator(col.accessor)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {paginatedData.map((row, idx) => (
                        <tr key={idx}>
                            {columns.map(col => {
                                const value = row[col.accessor];

                                if (col.Cell) {
                                    return <td key={col.accessor}>{col.Cell(row)}</td>;
                                }

                                if (col.accessor === 'polNo') {
                                    const isClickable = psm?.hasPolicyPortal === true;

                                    return (
                                        <td key={col.accessor}>
                                            {isClickable ? (
                                                <span
                                                    onClick={() => navigate(`/policy/${value}`)}
                                                    className="paginated-table-row-clickable"
                                                >
                                                    {value}
                                                </span>
                                            ) : (
                                                value
                                            )}
                                        </td>
                                    );
                                }

                                if (col.accessor === 'agtCode') {
                                    const isClickable = psm?.hasAgentPortal === true;

                                    return (
                                        <td key={col.accessor}>
                                            {isClickable ? (
                                                <span
                                                    onClick={() => navigate(`/agent/${value}`)}
                                                    className="paginated-table-row-clickable"
                                                >
                                                    {value}
                                                </span>
                                            ) : (
                                                value
                                            )}
                                        </td>
                                    );
                                }

                                return <td key={col.accessor}>{value}</td>;
                            })}
                        </tr>
                    ))}
                </tbody>

            </table>

            <div className="pagination-bar">
                <div className="pagination-summary">
                    Showing {from}–{to} of {totalEntries} entries
                </div>
                <div className="pagination-controls">
                    <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
                        «
                    </button>
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                        ‹
                    </button>

                    {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;

                        if (page < currentPage - 1 || page > currentPage + 1) return null;

                        return (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={currentPage === page ? 'active' : ''}
                            >
                                {page}
                            </button>
                        );
                    })}

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        ›
                    </button>
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                    >
                        »
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminSearchTable;
